import { useMemo } from "react";
import { resources } from "@constants/hardcoded-strings";
import { ClaimStatusTypes } from "@enums/claim-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { FormattedServiceAssertionPerformance } from "@idw-types/data/reports/service-assertion-performance";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./service-assertion-performance-comparison-table-container.less";
import { createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type ServiceAssertionPerformanceComparisonTableContainerProps = {
    handleClickOnTableCellLink: (claimStatusType: ClaimStatusTypes, serviceAssertionPerformance: FormattedServiceAssertionPerformance) => void;
    serviceAssertionPerformances: Array<FormattedServiceAssertionPerformance>;
    passGreenDiffRange: Array<number>;
    passYellowDiffRange: Array<number>;
    passRedDiffRange: Array<number>;
    unverifiedGreenDiffRange: Array<number>;
    unverifiedYellowDiffRange: Array<number>;
    unverifiedRedDiffRange: Array<number>;
    failGreenDiffRange: Array<number>;
    failYellowDiffRange: Array<number>;
    failRedDiffRange: Array<number>;
    tableState: TableState;
};

type ServiceAssertionPerformanceComparisonTableDataRow = FormattedServiceAssertionPerformance & {
    assertionShortName: string;
    serviceOfferingName: string;
};

export const ServiceAssertionPerformanceComparisonTableContainer = (props: ServiceAssertionPerformanceComparisonTableContainerProps) => {
    const {
        handleClickOnTableCellLink,
        serviceAssertionPerformances,
        passGreenDiffRange,
        passRedDiffRange,
        passYellowDiffRange,
        failGreenDiffRange,
        failRedDiffRange,
        failYellowDiffRange,
        unverifiedGreenDiffRange,
        unverifiedRedDiffRange,
        unverifiedYellowDiffRange,
        tableState
    } = props;
    const dispatch = useDispatch();

    const serviceAssertionPerformanceComparisonTableData: Array<ServiceAssertionPerformanceComparisonTableDataRow> = useMemo(() => {
        return serviceAssertionPerformances.map(sap => ({
            ...sap,
            assertionShortName: sap.apAssertion?.assertion?.shortName || "",
            serviceOfferingName: sap.apAssertion?.serviceOffering?.name || ""
        }));
    }, [serviceAssertionPerformances]);

    const columnHelper = createColumnHelper<ServiceAssertionPerformanceComparisonTableDataRow>();

    const columns = useMemo(() => ([
        columnHelper.group({
            "id": "first-col",
            header: "",
            enableSorting: false,
            columns: [
                columnHelper.accessor("assertionShortName", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.assertionName.header,
                    filterFn: "fuzzyText"
                }),
                columnHelper.accessor("serviceOfferingName", {
                    id: "sap-comparison-service",
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.service.header,
                    filterFn: "fuzzyText"
                })
            ]
        }),
        columnHelper.group({
            id: "second-col",
            header: resources.tables.serviceAssertionPerformanceComparison.columns.assertionPerformance.header,
            enableSorting: false,
            columns: [
                columnHelper.accessor("passAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.pass.header,
                    cell: (props) => {
                        const value = props.getValue();
                        if (value) {
                            let className = "";
                            const { original } = props.row;
                            const differenceFromBaseline = original.baselinePassAssertionPercent - original.passAssertionPercent;
                            if (differenceFromBaseline >= passGreenDiffRange[0] && differenceFromBaseline <= passGreenDiffRange[1]) {
                                className = styles.greenBackground;
                            } else if (differenceFromBaseline >= passYellowDiffRange[0] && differenceFromBaseline <= passYellowDiffRange[1]) {
                                className = styles.yellowBackground;
                            } else if (differenceFromBaseline >= passRedDiffRange[0] && differenceFromBaseline <= passRedDiffRange[1]) {
                                className = styles.redBackground;
                            }
                            return (
                                <span id={`pass-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.PASS, original)} className={className}>
                                    {value}
                                </span>
                            );
                        } else {
                            return resources.common.notFound;
                        }
                    }
                }),
                columnHelper.accessor("closeAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.close.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("partialAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.partial.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("unverifiedAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.unverified.header,
                    cell: (props) => {
                        const value = props.getValue();
                        if (value) {
                            let className = "";
                            const { original } = props.row;
                            const differenceFromBaseline = original.baselineUnverifiedAssertionPercent - original.unverifiedAssertionPercent;
                            if (differenceFromBaseline >= unverifiedGreenDiffRange[0] && differenceFromBaseline <= unverifiedGreenDiffRange[1]) {
                                className = styles.greenBackground;
                            } else if (differenceFromBaseline >= unverifiedYellowDiffRange[0] && differenceFromBaseline <= unverifiedYellowDiffRange[1]) {
                                className = styles.yellowBackground;
                            } else if (differenceFromBaseline >= unverifiedRedDiffRange[0] && differenceFromBaseline <= unverifiedRedDiffRange[1]) {
                                className = styles.redBackground;
                            }
                            return (
                                <span id={`unverified-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.UNVERIFIED, original)} className={className}>
                                    {value}
                                </span>
                            );
                        } else {
                            return resources.common.notFound;
                        }
                    }
                }),
                columnHelper.accessor("failAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.fail.header,
                    cell: (props) => {
                        const value = props.getValue();
                        if (value) {
                            let className = "";
                            const { original } = props.row;
                            const differenceFromBaseline = original.baselineFailAssertionPercent - original.failAssertionPercent;
                            if (differenceFromBaseline >= failGreenDiffRange[0] && differenceFromBaseline <= failGreenDiffRange[1]) {
                                className = styles.greenBackground;
                            } else if (differenceFromBaseline >= failYellowDiffRange[0] && differenceFromBaseline <= failYellowDiffRange[1]) {
                                className = styles.yellowBackground;
                            } else if (differenceFromBaseline >= failRedDiffRange[0] && differenceFromBaseline <= failRedDiffRange[1]) {
                                className = styles.redBackground;
                            }
                            return (
                                <span id={`fail-${props.row.original.assertionShortName}-${props.row.index}`} onClick={() => handleClickOnTableCellLink(ClaimStatusTypes.FAIL, original)} className={className}>
                                    {value}
                                </span>
                            );
                        } else {
                            return resources.common.notFound;
                        }
                    }
                })
            ]
        }),
        columnHelper.group({
            id: "third-col",
            header: resources.tables.serviceAssertionPerformanceComparison.columns.benchmarkAssertionPerformance.header,
            enableSorting: false,
            columns: [
                columnHelper.accessor("baselinePassAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.pass.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("baselineCloseAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.close.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("baselinePartialAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.partial.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("baselineUnverifiedAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.unverified.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                }),
                columnHelper.accessor("baselineFailAssertionPercent", {
                    header: resources.tables.serviceAssertionPerformanceComparison.columns.fail.header,
                    cell: (props) => props.getValue() ? props.getValue() : resources.common.notFound
                })
            ]
        })
    ]), [serviceAssertionPerformanceComparisonTableData]);

    const useTableResult = useReactTable({
        columns,
        data: serviceAssertionPerformanceComparisonTableData,
        state: tableState,
        filterFns: null,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE_COMPARISON, tableState.sorting, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.SERVICE_ASSERTION_PERFORMANCE_COMPARISON, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.serviceAssertionPerformanceComparisonTableContainer}>
            <IdwTable
                id="service-assertion-performance-comparison"
                className={styles.serviceAssertionPerformanceComparisonTable}
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"service-assertion-performance-comparison"}
            />
            <PaginationContainer
                idPrefix="service-assertion-performance-comparison"
                useTableResult={useTableResult}
            />
        </IdwTableContainer>
    );
};